import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import EmployeeModal from '../components/EmployeeModal';
import EmployeeDetail from '../components/EmployeeDetail';
import ConfirmDialog from '../components/ConfirmDialog';
import HistoryModal from '../components/HistoryModal';
import { SearchIcon, PlusIcon, EyeIcon, EditIcon, ChevronLeftIcon, ChevronRightIcon, UsersIcon } from '../components/Icons';

const EMPTY_FORM = {
  name: '', project: '', gender: 'Male', designation: '', joiningDate: '', dob: '',
  iqamaNo: '', iqamaExpiry: '', qiwaStart: '', qiwaEnd: '', passportNo: '', passportExpiry: '', healthInsuranceExpiry: '', deductionBalance: '', nationality: '',
  basicSalary: '', allowance: '', totalSalary: '', contact: '',
  sponsorship: '', iban: '', iqamaStatus: 'Renewed', status: 'Active', comments: ''
};

function avatarGradient(name = '') {
  const g = ['linear-gradient(135deg,#667eea,#764ba2)','linear-gradient(135deg,#fb866d,#e8614a)','linear-gradient(135deg,#1a2744,#2d3f6b)','linear-gradient(135deg,#22c55e,#16a34a)','linear-gradient(135deg,#f59e0b,#d97706)','linear-gradient(135deg,#8b5cf6,#7c3aed)'];
  let h = 0; for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffffffff;
  return g[Math.abs(h) % g.length];
}
function initials(name = '') { return name.split(' ').slice(0,2).map(n=>n[0]||'').join('').toUpperCase(); }
function getAlert(emp) {
  if (!emp.iqamaExpiry) return null;
  const d = Math.ceil((new Date(emp.iqamaExpiry) - new Date()) / 86400000);
  if (d < 0) return { level: 'expired', days: d };
  if (d <= 30) return { level: 'critical', days: d };
  if (d <= 90) return { level: 'warning', days: d };
  return null;
}
const alertText = { expired: 'text-red-600 font-semibold', critical: 'text-[#e8614a] font-semibold', warning: 'text-amber-600 font-semibold' };

const Pill = ({ tone, children }) => {
  const tones = {
    green: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    red: 'bg-red-50 text-red-500 border-red-200',
    gray: 'bg-slate-50 text-slate-500 border-slate-200',
    blue: 'bg-blue-50 text-blue-500 border-blue-200',
  };
  return <span className={`inline-flex px-3 py-1 rounded-full text-[11.5px] font-semibold border ${tones[tone]}`}>{children}</span>;
};

export default function Employees() {
  const { canEdit } = useAuth();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [sponsorships, setSponsorships] = useState([]);
  const [search, setSearch] = useState('');
  const [filterProject, setFP] = useState('All');
  const [filterStatus, setFS] = useState('All');
  const [filterIqama, setFI] = useState('All');
  const [filterSponsor, setFSp] = useState('All');
  const [showAdd, setShowAdd] = useState(false);
  const [editEmp, setEditEmp] = useState(null);
  const [detailEmp, setDetailEmp] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [removeEmp, setRemoveEmp] = useState(null);
  const [historyEmp, setHistoryEmp] = useState(null);
  const [removing, setRemoving] = useState(false);
  const LIMIT = 15;

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, limit: LIMIT, search };
    if (filterProject !== 'All') params.project = filterProject;
    if (filterStatus !== 'All') params.status = filterStatus;
    if (filterIqama !== 'All') params.iqamaStatus = filterIqama;
    if (filterSponsor !== 'All') params.sponsorship = filterSponsor;
    api.get('/employees', { params })
      .then(r => { setData(r.data.data); setTotal(r.data.total); setTotalPages(r.data.totalPages); })
      .finally(() => setLoading(false));
  }, [page, search, filterProject, filterStatus, filterIqama, filterSponsor]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/projects').then(r => setProjects(['All', ...r.data]));
    api.get('/sponsorships').then(r => setSponsorships(['All', ...r.data]));
  }, []);

  async function handleSave(form) {
    setSaving(true); setSaveError('');
    try {
      if (editEmp) await api.put(`/employees/${editEmp.id}`, form);
      else await api.post('/employees', form);
      setShowAdd(false); setEditEmp(null); load();
    } catch (err) {
      setSaveError(err.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  }

  async function handleDelete() {
    setRemoving(true);
    try {
      await api.delete(`/employees/${removeEmp.id}`);
      setRemoveEmp(null); setDetailEmp(null); load();
    } finally { setRemoving(false); }
  }

  const hasFilters = search || filterProject !== 'All' || filterStatus !== 'All' || filterIqama !== 'All' || filterSponsor !== 'All';
  const pages = [];
  const s = Math.max(1, page - 2), e2 = Math.min(totalPages, s + 4);
  for (let i = s; i <= e2; i++) pages.push(i);

  const selCls = "px-3.5 py-2.5 border border-slate-200 rounded-xl text-[13px] bg-white text-slate-600 outline-none focus:border-[#fb866d] cursor-pointer transition-colors";

  return (
    <div>
      {/* Topbar */}
      <div className="hidden lg:flex bg-white border-b border-slate-100 px-8 py-4 items-center justify-between sticky top-0 z-40">
        <h1 className="text-lg font-bold text-slate-800">Employees</h1>
        <span className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-full text-xs font-semibold text-slate-600">Admin</span>
      </div>

      <div className="p-4 lg:p-8 stagger">
        {/* Page header card */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#0f1b33] flex items-center justify-center">
              <UsersIcon size={22} className="text-white" />
            </div>
            <div>
              <h2 className="text-[17px] font-bold text-slate-800">Workforce Registry</h2>
              <p className="text-[13px] text-slate-400">{total} records — search, filter, and manage all workers.</p>
            </div>
          </div>
          {canEdit && (
            <button onClick={() => { setEditEmp(null); setShowAdd(true); }}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-[13px] font-semibold bg-[#fb866d] hover:bg-[#e8614a] transition-all shadow-sm">
              <PlusIcon size={15} /> Add Employee
            </button>
          )}
        </div>

        {/* Filters bar */}
        <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-4 flex items-center gap-3 flex-wrap" style={{ boxShadow: '0 1px 3px rgba(15,27,51,0.06)' }}>
          <div className="relative flex-1 min-w-[220px]">
            <SearchIcon size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl text-[13.5px] outline-none bg-white text-slate-700 focus:border-[#fb866d] transition-all placeholder:text-slate-400"
              placeholder="Search name, Iqama, project, sponsorship..."
              value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
          <select className={selCls} value={filterProject} onChange={e => { setFP(e.target.value); setPage(1); }}>
            <option value="All">All projects</option>
            {projects.filter(p => p !== 'All').map(p => <option key={p}>{p}</option>)}
          </select>
          <select className={selCls} value={filterSponsor} onChange={e => { setFSp(e.target.value); setPage(1); }}>
            <option value="All">All sponsorships</option>
            {sponsorships.filter(s => s !== 'All').map(s => <option key={s}>{s}</option>)}
          </select>
          <select className={selCls} value={filterStatus} onChange={e => { setFS(e.target.value); setPage(1); }}>
            <option value="All">All statuses</option>
            <option>Active</option><option>Terminated</option>
          </select>
          <select className={selCls} value={filterIqama} onChange={e => { setFI(e.target.value); setPage(1); }}>
            <option value="All">All Iqama</option>
            <option>Renewed</option><option>Expired</option><option>Missing</option>
          </select>
          {hasFilters && (
            <button onClick={() => { setSearch(''); setFP('All'); setFS('All'); setFI('All'); setFSp('All'); setPage(1); }}
              className="px-4 py-2.5 border border-slate-200 rounded-xl text-xs font-semibold text-slate-500 hover:border-red-300 hover:text-red-500 transition-all">
              Clear filters
            </button>
          )}
        </div>

        {/* Table card */}
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden" style={{ boxShadow: '0 1px 3px rgba(15,27,51,0.06)' }}>
          <div className="overflow-x-auto">
            <table className="w-full" style={{ minWidth: 1050 }}>
              <thead>
                <tr className="bg-slate-50/70">
                  {['Employee','Project / Client','Designation','Sponsorship','Nationality','Iqama Expiry','Iqama','Gross (SAR)','Salary (SAR)','History','Status',''].map(h => (
                    <th key={h} className="px-4 py-3.5 text-left text-[11px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100 whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={12} className="text-center py-12 text-slate-400 text-sm">
                    <div className="inline-flex items-center gap-2.5">
                      <div className="w-4 h-4 border-2 border-slate-200 border-t-[#fb866d] rounded-full animate-spin" />
                      Loading...
                    </div>
                  </td></tr>
                ) : data.length === 0 ? (
                  <tr><td colSpan={12} className="text-center py-16">
                    <div className="text-[15px] font-semibold text-slate-500">No employees found</div>
                    <div className="text-sm text-slate-400 mt-1">Try adjusting your filters</div>
                  </td></tr>
                ) : data.map(emp => {
                  const alert = getAlert(emp);
                  return (
                    <tr key={emp.id} className="border-b border-slate-50 odd:bg-slate-50/50 hover:bg-[#fde8e3]/40 transition-colors last:border-0">
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center text-[11px] font-bold text-white"
                            style={{ background: avatarGradient(emp.name) }}>
                            {initials(emp.name)}
                          </div>
                          <div>
                            <div className="text-[13.5px] font-semibold text-slate-800">{emp.name}</div>
                            <div className="text-[11px] text-slate-400 font-mono">#{String(emp.id).padStart(4,'0')}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 max-w-[140px] truncate text-[13px] text-slate-600">{emp.project || '—'}</td>
                      <td className="px-4 py-3.5 max-w-[130px] truncate text-[13px] text-slate-500">{emp.designation || '—'}</td>
                      <td className="px-4 py-3.5 max-w-[130px] truncate text-[13px] text-slate-600">{emp.sponsorship || '—'}</td>
                      <td className="px-4 py-3.5 text-[13px] text-slate-600">{emp.nationality || '—'}</td>
                      <td className="px-4 py-3.5">
                        {emp.iqamaExpiry ? (
                          <span className={`text-[12.5px] ${alert ? alertText[alert.level] : 'text-slate-600'}`}>
                            {emp.iqamaExpiry}
                            {alert && <span className="ml-1 text-[10.5px] opacity-75">({alert.days < 0 ? `${Math.abs(alert.days)}d ago` : `${alert.days}d`})</span>}
                          </span>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-3.5">
                        {emp.iqamaStatus === 'Renewed' && <Pill tone="green">Renewed</Pill>}
                        {emp.iqamaStatus === 'Expired' && <Pill tone="red">Expired</Pill>}
                        {!emp.iqamaStatus && <Pill tone="gray">Missing</Pill>}
                      </td>
                      <td className="px-4 py-3.5 text-[13px] text-slate-600">{((emp.basicSalary || 0) + (emp.allowance || 0)).toLocaleString()}</td>
                      <td className="px-4 py-3.5 text-[13.5px] font-semibold text-slate-800">{(emp.totalSalary || 0).toLocaleString()}</td>
                      <td className="px-4 py-3.5">
                        <button onClick={() => setHistoryEmp(emp)}
                          className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-all ${
                            (emp.history?.length || 0) > 0
                              ? 'border-[#fb866d]/40 text-[#e8614a] bg-[#fde8e3]/50 hover:bg-[#fde8e3]'
                              : 'border-slate-200 text-slate-400 hover:border-slate-300'
                          }`}>
                          {(emp.history?.length || 0)} {(emp.history?.length || 0) === 1 ? 'change' : 'changes'}
                        </button>
                      </td>
                      <td className="px-4 py-3.5">
                        {emp.status === 'Active' ? <Pill tone="blue">Active</Pill> : <Pill tone="red">Terminated</Pill>}
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex gap-1.5">
                          <button onClick={() => setDetailEmp(emp)} title="View"
                            className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:border-[#fb866d] hover:text-[#e8614a] hover:bg-[#fde8e3] transition-all">
                            <EyeIcon size={14} />
                          </button>
                          {canEdit && (
                            <button onClick={() => { setEditEmp(emp); setShowAdd(true); }} title="Edit"
                              className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:border-[#fb866d] hover:text-[#e8614a] hover:bg-[#fde8e3] transition-all">
                              <EditIcon size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Footer / pagination */}
          <div className="flex items-center justify-between px-6 py-3.5 border-t border-slate-100">
            <span className="text-[12.5px] text-slate-400">
              {total === 0 ? 'No results' : `Showing ${(page-1)*LIMIT+1}–${Math.min(page*LIMIT,total)} of ${total}`}
            </span>
            <div className="flex gap-1.5">
              <button onClick={() => setPage(p => Math.max(1,p-1))} disabled={page===1}
                className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 hover:border-[#fb866d] hover:text-[#e8614a] disabled:opacity-40 disabled:cursor-not-allowed transition-all">
                <ChevronLeftIcon size={14} />
              </button>
              {pages.map(p => (
                <button key={p} onClick={() => setPage(p)}
                  className={`w-8 h-8 rounded-lg border text-[13px] font-semibold transition-all ${
                    page === p ? 'bg-[#fb866d] border-[#fb866d] text-white' : 'border-slate-200 text-slate-600 hover:border-[#fb866d] hover:text-[#e8614a]'
                  }`}>
                  {p}
                </button>
              ))}
              <button onClick={() => setPage(p => Math.min(totalPages,p+1))} disabled={page===totalPages}
                className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 hover:border-[#fb866d] hover:text-[#e8614a] disabled:opacity-40 disabled:cursor-not-allowed transition-all">
                <ChevronRightIcon size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {showAdd && (
        <EmployeeModal
          error={saveError}
          initial={editEmp || EMPTY_FORM}
          projects={projects.filter(p => p !== 'All')}
          sponsorships={sponsorships.filter(s => s !== 'All')}
          saving={saving}
          onSave={handleSave}
          onClose={() => { setShowAdd(false); setEditEmp(null); }}
          isEdit={!!editEmp}
        />
      )}
      <ConfirmDialog
        open={!!removeEmp}
        tone="danger"
        title={`Remove ${removeEmp?.name}?`}
        message="This permanently deletes the employee record, including salary and document details."
        confirmLabel="Remove Employee"
        busy={removing}
        onConfirm={handleDelete}
        onClose={() => !removing && setRemoveEmp(null)}
      />
      {detailEmp && (
        <EmployeeDetail
          emp={detailEmp}
          onClose={() => setDetailEmp(null)}
          onEdit={() => { setEditEmp(detailEmp); setDetailEmp(null); setShowAdd(true); }}
          onDelete={() => setRemoveEmp(detailEmp)}
          onHistory={() => { setHistoryEmp(detailEmp); }}
        />
      )}
      {historyEmp && <HistoryModal emp={historyEmp} onClose={() => setHistoryEmp(null)} />}
    </div>
  );
}